import api from './api';

export interface PageLink {
  text: string;
  url: string;
}

export interface PageSection {
  header: string;
  links: PageLink[];
}

export interface PageContent {
  id: string;
  slug: string;
  title: string;  // Keep for display purposes only
  bannerImageUrl?: string;
  sections?: PageSection[];
  createdAt: string;
  updatedAt: string;
}

const pageContentService = {
  async getAllPages(params?: { page?: number; limit?: number }): Promise<{
    data: PageContent[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }> {
    const response = await api.get<{
      data: PageContent[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>('/page-content', { params });
    return response.data;
  },

  async getPageBySlug(slug: string): Promise<PageContent> {
    const response = await api.get<PageContent>(`/page-content/${slug}`);
    return response.data;
  },

  async createPage(data: Omit<PageContent, 'id' | 'createdAt' | 'updatedAt'>): Promise<PageContent> {
    const response = await api.post<PageContent>('/page-content', data);
    return response.data;
  },

  async updatePage(id: string, data: Partial<Omit<PageContent, 'id' | 'createdAt' | 'updatedAt'>>): Promise<PageContent> {
    const response = await api.put<PageContent>(`/page-content/${id}`, data);
    return response.data;
  },

  async deletePage(id: string): Promise<void> {
    await api.delete(`/page-content/${id}`);
  },

  async uploadBanner(file: File, onProgress?: (progress: number) => void): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('banner', file);

    const response = await api.post<{ url: string }>('/page-content/upload-banner', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    });
    return response.data;
  },

  async getBannerUrl(pageId: string): Promise<string> {
    // Return the API endpoint URL directly - the endpoint will redirect to the signed URL
    const baseURL = api.defaults.baseURL || '';
    return `${baseURL}/page-content/banner/${pageId}`;
  },

  async deleteBanner(pageId: string): Promise<void> {
    await api.delete(`/page-content/${pageId}/banner`);
  }
};

export default pageContentService;